import { useEffect, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CustomButton } from '@/components/uiWrappers/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampusResourceData, College } from '@mightybyte/business';

interface CampusResourceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pass existing resource to switch to edit mode */
  resource?: CampusResourceData;
}

const UNIVERSITY_LABELS: Record<College, string> = {
  [College.LMU]: 'Loyola Marymount University (LMU)',
  [College.Lehigh]: 'Lehigh University',
};

export function CampusResourceFormModal({
  isOpen,
  onClose,
  resource,
}: CampusResourceFormModalProps) {
  const utils = trpc.useUtils();
  const isEditing = !!resource;

  const [title, setTitle] = useState('');
  const [university, setUniversity] = useState<College>(College.LMU);
  const [link, setLink] = useState('');
  const [order, setOrder] = useState('0');

  // Reset form when modal opens/closes or resource changes
  useEffect(() => {
    if (isOpen) {
      if (resource) {
        setTitle(resource.title);
        setUniversity(resource.university);
        setLink(resource.link);
        setOrder(resource.order.toString());
      } else {
        setTitle('');
        setUniversity(College.LMU);
        setLink('');
        setOrder('0');
      }
    }
  }, [isOpen, resource]);

  const createMutation = trpc.admin.campusResource.addCampusResource.useMutation({
    onSuccess: () => {
      utils.admin.campusResource.getCampusResources.invalidate();
      onClose();
      toast.success('Campus resource created successfully!');
    },
    onError: (err) => {
      toast.error(`Failed to create campus resource: ${err.message}`);
    },
  });

  const updateMutation = trpc.admin.campusResource.updateCampusResource.useMutation({
    onSuccess: () => {
      utils.admin.campusResource.getCampusResources.invalidate();
      onClose();
      toast.success('Campus resource updated successfully!');
    },
    onError: (err) => {
      toast.error(`Failed to update campus resource: ${err.message}`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const orderNum = parseInt(order, 10);
    if (isNaN(orderNum) || orderNum < 0) {
      toast.error('Order must be a valid number (0 or greater)');
      return;
    }

    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!link.trim()) {
      toast.error('Link is required');
      return;
    }

    // Validate URL format
    try {
      new URL(link);
    } catch {
      toast.error('Link must be a valid URL');
      return;
    }

    if (isEditing && resource) {
      updateMutation.mutate({
        id: resource._id,
        title: title.trim(),
        university,
        link: link.trim(),
        order: orderNum,
      });
    } else {
      createMutation.mutate({
        title: title.trim(),
        university,
        link: link.trim(),
        order: orderNum,
      });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Campus Resource' : 'Create Campus Resource'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <Label htmlFor="title" className="required">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Health & Wellness Center"
              maxLength={200}
              required
            />
          </div>

          {/* University */}
          <div className={'w-full'}>
            <Label htmlFor="university" className="required">University</Label>
            <Select value={university} onValueChange={(value) => setUniversity(value as College)}>
              <SelectTrigger className="w-full h-auto min-h-10 px-3 py-2 rounded-lg focus-visible:ring-0.5 focus-visible:ring-brand">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(UNIVERSITY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Link */}
          <div>
            <Label htmlFor="link" className="required">Link (URL)</Label>
            <Input
              id="link"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://example.com/resource"
              required
            />
          </div>

          {/* Order */}
          <div>
            <Label htmlFor="order">Order</Label>
            <Input
              id="order"
              type="number"
              min="0"
              step="1"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Resources are sorted by order (ascending), then by creation date for duplicates.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <CustomButton
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </CustomButton>
            <CustomButton
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </CustomButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
